import { useId } from "react";
import {
  CheckCircle2,
  CircleSlash,
  ExternalLink,
  History,
  type LucideIcon,
} from "lucide-react";
import { formatDate } from "../../lib/format-date";
import { stateDisplayName } from "./state-names";
import { safeSourceUrl } from "../verification-source-url";
import { SourceResultRow } from "./source-result-row";
import { AllStatesTable } from "./state-exclusions-group/all-states-table";
import { StatesWithoutList } from "./state-exclusions-group/states-without-list";
import {
  pluralizeStates,
  stateCheckView,
  stateNames,
  stateRows,
  type SourceCheck,
  type StateRow,
} from "./state-exclusions-group/state-rows";
import { groupStatesByResult, StateResult } from "./state-exclusions-status";
import type { ReportItem, RunScope } from "./types";

const SECTION_HEADING = "text-xs font-semibold";

/** An unscreened state has no snapshot, so the reviewer searches the state's
 * own site; it is a third-party source the app holds no copy of. A missing or
 * unsafe URL falls back to the plain name. */
function OfficialListLink({ row }: { row: StateRow }) {
  const name = stateDisplayName(row.source.stateCode);
  const href = safeSourceUrl(row.source.sourceUrl);
  if (!href) {
    return <>{name}</>;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Search the ${name} Medicaid exclusion list (opens in new tab)`}
      className="inline-flex items-center gap-0.5 underline"
    >
      {name}
      <ExternalLink className="size-3 shrink-0" aria-hidden />
    </a>
  );
}

function NoticeLine({
  rows,
  title,
  guidance,
  icon: Icon = CircleSlash,
  linkToOfficialList = false,
  showListDate = false,
}: {
  rows: StateRow[];
  title: string;
  guidance: string;
  icon?: LucideIcon;
  linkToOfficialList?: boolean;
  /** Shows when each state's older list was last confirmed current. */
  showListDate?: boolean;
}) {
  if (rows.length === 0) {
    return null;
  }
  return (
    <div className="space-y-0.5">
      <p className="flex items-center gap-1.5 font-semibold">
        <Icon className="size-3.5 shrink-0" aria-hidden />
        {pluralizeStates(rows.length)} {title}
      </p>
      {linkToOfficialList ? (
        <ul className="flex flex-wrap gap-x-3 gap-y-1 leading-5">
          {rows.map((row) => (
            <li key={row.source.resultKey}>
              <OfficialListLink row={row} />
              {showListDate && row.source.confirmedAt && (
                <> · list from {formatDate(row.source.confirmedAt)}</>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="leading-5">{stateNames(rows).join(", ")}</p>
      )}
      <p className="leading-5">{guidance}</p>
    </div>
  );
}

export function StateExclusionsDetails({
  check,
  runId,
  scope,
  issues,
}: {
  check: SourceCheck;
  runId: string;
  scope: RunScope;
  /** The run item's issues; each state's row shows its own. */
  issues?: ReportItem["issues"];
}) {
  const attentionHeadingId = useId();
  const clearHeadingId = useId();
  const rows = stateRows(check);

  if (rows.length === 0) {
    // The shared check hasn't collected yet (queued, running, failed, or
    // blocked), so there is nothing to break out by state.
    return <SourceResultRow check={check} runId={runId} scope={scope} />;
  }

  const groups = groupStatesByResult(rows);
  const attention = groups[StateResult.AttentionNeeded];
  const notScreened = groups[StateResult.NotScreened];
  const olderList = groups[StateResult.OlderList];
  const limited = groups[StateResult.Limited];
  const clear = groups[StateResult.Clear];
  const hasNotice =
    notScreened.length > 0 || olderList.length > 0 || limited.length > 0;
  return (
    <section aria-label="State Medicaid exclusions" className="divide-y">
      <p className="px-3 py-2 text-xs leading-5 text-muted-foreground">
        Checked {pluralizeStates(rows.length)}, including states without a saved
        license. Separate from license verification.
      </p>
      {attention.length > 0 && (
        <section aria-labelledby={attentionHeadingId}>
          <h5
            id={attentionHeadingId}
            className={`${SECTION_HEADING} px-3 pt-3 text-amber-900`}
          >
            {pluralizeStates(attention.length)} with possible exclusions: review
            before accepting
          </h5>
          <div className="divide-y">
            {attention.map(({ source, item }) => (
              <SourceResultRow
                key={source.resultKey}
                check={stateCheckView({ check, source, item })}
                runId={runId}
                scope={scope}
                resultKey={source.resultKey}
                issues={issues}
              />
            ))}
          </div>
        </section>
      )}
      {hasNotice && (
        <div className="space-y-3 p-3 text-xs text-amber-900">
          <NoticeLine
            rows={notScreened}
            title="not screened"
            guidance="There's no saved copy of these lists, so the provider wasn't searched in these states. To cover them, search each state's official list and record what you found under Add note or evidence."
            linkToOfficialList
          />
          <NoticeLine
            rows={olderList}
            title="checked against an older list"
            guidance="These lists couldn't be refreshed recently. No exclusions were found on the copies we have, but newer ones may be missing. To confirm, search each state's official list and record what you found under Add note or evidence."
            icon={History}
            linkToOfficialList
            showListDate
          />
          <NoticeLine
            rows={limited}
            title="with limitations"
            guidance="Results may be incomplete. Open the state below to see why."
          />
        </div>
      )}
      {clear.length > 0 && (
        <section aria-labelledby={clearHeadingId} className="space-y-1 p-3">
          <h5
            id={clearHeadingId}
            className={`${SECTION_HEADING} flex items-center gap-1.5`}
          >
            <CheckCircle2
              className="size-3.5 shrink-0 text-green-800"
              aria-hidden
            />
            {pluralizeStates(clear.length)}: no exclusions found
          </h5>
          <p className="text-xs leading-5 text-muted-foreground">
            {stateNames(clear).join(", ")}
          </p>
        </section>
      )}
      <AllStatesTable
        rows={rows}
        check={check}
        runId={runId}
        scope={scope}
        issues={issues}
      />
      <StatesWithoutList />
    </section>
  );
}
